import { Box, Flex, Text } from '@chakra-ui/react';
import { CMXIcon } from '@codametrix/ui-components';
import React from 'react';
import type { SlotProps } from '../../../project_starburst';
import {
  STRIP_PAD_X,
  TAB_ACTIVE_BG,
  TAB_ACTIVE_BORDER,
  TAB_BORDER_STYLE,
  TAB_BORDER_WIDTH,
  TAB_FONT_SIZE,
  TAB_GAP,
  TAB_PAD_X,
  TAB_PAD_Y,
  TAB_RADIUS,
  TAB_WEIGHT,
  TAB_WEIGHT_ACTIVE
} from '../../../project_starburst';
import type { NotesData } from '../stateMappers/caseRunToCodingScreenState';
import { useNoteInteraction } from './noteInteractionContext';
import { iconForNoteType } from './noteTypeIcon';

function asNotesData(value: unknown): NotesData {
  return value != null &&
    typeof value === 'object' &&
    Array.isArray((value as NotesData).notes)
    ? (value as NotesData)
    : { notes: [] };
}

function noteKey(note: CMxCommonApp.DecoratedNote): string {
  return String(note.document_id);
}

/**
 * The note-navigation strip (`left.docNav`'s `'NoteNav'` slot) -- one entry per note
 * (Note Type + icon, AC4/AC5/AC6), rendered even when there is exactly ONE note (AC12 -- no
 * length-gated early return the way the static tab strip has one). Clicking a note's TYPE LABEL
 * makes it the active note (`setActiveNoteKey`, shared with `NoteViewer` via
 * `useNoteInteraction()` -- AC8's "navigate between notes"); clicking its ICON instead opens the
 * source-document modal for THAT note (AC7) without changing which note is active. Empty-state
 * (AC13) when `notes` is empty.
 *
 * STYLING: type and spacing come from `project_starburst/styles/tokens.ts` (`TAB_*`/`STRIP_PAD_X`)
 * rather than Chakra's own scale, so this strip matches the production-coding-ux prototype's
 * `.doc-tab` rhythm (12px label, 500 resting / 600 selected, 6px strip inset). Chakra scale INTS were
 * the problem being fixed: the app forces a 14px root, so the former `fontSize="sm"`/`px={2} py={1}`/
 * `gap={2}` resolved to 12.25px/7px/3.5px/7px -- close enough to look deliberate, far enough off to
 * read as a different component from the prototype, and with no weight change on selection at all
 * (Chakra sets none, so both states inherited the body's 400). The pill SHAPE is unchanged and
 * deliberately not the prototype's underline tab -- that is a shape decision, not a spacing one.
 * The selected pill's blue fill/border also moved out of this file into `TAB_ACTIVE_BG`/
 * `TAB_ACTIVE_BORDER` -- unchanged in value, but the fill is now shared with both static tab strips
 * so a selected tab reads the same on either side of the shell.
 *
 * `flex="1" minW={0}` because `DocumentTitleNav`'s slot wrapper is a centering FLEX container (it seats
 * this strip vertically in the band, which only the shell knows the height of). Without them this
 * would be a content-width flex ITEM rather than the full-width block child it used to be, and
 * `overflowX="auto"` needs a bounded width to scroll within -- `minW={0}` is what lets a flex item
 * actually shrink below its content's intrinsic width instead of forcing the container wider.
 */
export const NoteNav: React.FC<SlotProps> = ({ value }) => {
  const { notes } = asNotesData(value);
  const { activeNoteKey, setActiveNoteKey, openSourceModal } =
    useNoteInteraction();

  if (notes.length === 0) {
    return (
      <Flex
        role="region"
        aria-label="Note navigation"
        data-testid="note-nav-empty"
        align="center"
        px={STRIP_PAD_X}
      >
        <Text fontSize={TAB_FONT_SIZE} color="gray.500">
          No notes available
        </Text>
      </Flex>
    );
  }

  const activeKey = activeNoteKey ?? noteKey(notes[0]);

  return (
    <Flex
      role="tablist"
      aria-label="Note navigation"
      data-testid="note-nav"
      align="center"
      flex="1"
      minW={0}
      gap={TAB_GAP}
      px={STRIP_PAD_X}
      overflowX="auto"
    >
      {notes.map((note) => {
        const key = noteKey(note);
        const isActive = key === activeKey;
        const label = note.filename ?? 'Document';
        return (
          <Flex
            key={key}
            role="tab"
            aria-selected={isActive}
            data-testid="note-nav-item"
            data-note-key={key}
            align="center"
            gap={1}
            px={TAB_PAD_X}
            py={TAB_PAD_Y}
            borderWidth={TAB_BORDER_WIDTH}
            borderStyle={TAB_BORDER_STYLE}
            borderColor={isActive ? TAB_ACTIVE_BORDER : 'transparent'}
            borderRadius={TAB_RADIUS}
            bg={isActive ? TAB_ACTIVE_BG : 'transparent'}
          >
            <Text
              as="button"
              type="button"
              border="none"
              bg="transparent"
              p={0}
              m={0}
              font="inherit"
              fontSize={TAB_FONT_SIZE}
              fontWeight={isActive ? TAB_WEIGHT_ACTIVE : TAB_WEIGHT}
              cursor="pointer"
              _focusVisible={{ outline: '2px solid', outlineColor: 'blue.400' }}
              onClick={() => setActiveNoteKey(key)}
            >
              {label}
            </Text>
            <Box
              as="button"
              type="button"
              aria-label={`Open source document for ${label}`}
              data-testid="note-nav-icon"
              display="inline-flex"
              alignItems="center"
              border="none"
              bg="transparent"
              p={0}
              m={0}
              cursor="pointer"
              _focusVisible={{ outline: '2px solid', outlineColor: 'blue.400' }}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                openSourceModal(key);
              }}
            >
              <CMXIcon
                svgIconName={iconForNoteType(note.type)}
                svgIconSize="16"
              />
            </Box>
          </Flex>
        );
      })}
    </Flex>
  );
};
